import axios from "axios";
import { baseHRAdminApi, baseMvteApi } from "src/config";
import { Applicant } from "src/apps/hr-config-management/types/Applicant";
import { HRAssignee } from "src/apps/hr-config-management/types/HRAssignee";
import { JobMaster } from "src/apps/hr-config-management/types/JobMaster";
import { OrgPayload } from "src/apps/hr-config-management/types/OrgPayload";
import { RepresentativeRaw } from "src/apps/hr-config-management/types/RepresentativeRaw";
import { WorksitePayload } from "src/apps/hr-config-management/types/WorksitePayload";
import { WorksiteRepresentativeRaw } from "src/apps/hr-config-management/types/WorksiteRepresentativeRaw";
import { DriverSuperCreatePayload } from "src/apps/hr-config-management/types/DriverSuperCreatePayload";
import { DriverSuperUpdatePayload } from "src/apps/hr-config-management/types/DriverSuperUpdatePayload";
import { Supervisor } from "src/apps/hr-config-management/types/Supervisor";
import { SupervisorMap } from "src/apps/hr-config-management/types/SupervisorMap";
import { YellowMarkedRaw } from "src/apps/hr-config-management/types/YellowMarkRaw";
import { YellowTenstreetApplicant } from "src/apps/hr-config-management/types/YellowTensreetApplicant";
import { TerminationWorkflow } from "src/apps/hr-config-management/types/TerminationWorkflow";
import { LastWorkDatePayload } from "src/apps/hr-config-management/types/LastWorkDatePayload";
import { TerminationCategory } from "src/apps/hr-config-management/types/TerminationCategory";
import { TerminationReason } from "src/apps/hr-config-management/types/TerminationReason";
import { TerminationReasonPayload } from "src/apps/hr-config-management/types/TerminationReasonPayload";
import { TerminationCategoryPayload } from "src/apps/hr-config-management/types/TerminationCategoryPayload";
import { TerminationNotesPayload } from "src/apps/hr-config-management/types/TerminationNotesPayload";
import { WorksiteOrgRelationshipPayload } from "src/apps/hr-config-management/types/WorksiteOrgRelationshipPayload";
import { CreateWorksitePayload } from "src/apps/hr-config-management/types/CreateWorksitePayload";
import { Worksites } from "src/apps/hr-config-management/types/Worksites";
import { OrgWorksiteRelationship } from "src/apps/hr-config-management/types/OrgWorksiteRelationship";

const hraApiInstance = axios.create({
    baseURL: baseHRAdminApi,
});

const mvteApiInstance = axios.create({
    baseURL: baseMvteApi,
});

async function getHRAssignee()
{
    const response = await hraApiInstance.get<HRAssignee[]>(
            `/hra/assignees`,
        );
    
    return response.data;
}

async function getOrgRepresentativeRaw()
{
    const response = await hraApiInstance.get<RepresentativeRaw[]>(
            `/hra/reps`,
        );
    return response.data
}

async function getWorksiteRepresentativeRaw()
{
    const response = await hraApiInstance.get<WorksiteRepresentativeRaw[]>(
            `/hra/worksiteReps`,
        );
    
    return response.data;
}

async function getYellow()
{
    const response = await mvteApiInstance.get<YellowMarkedRaw[]>(
            `/engauge/yellow`,
        );
    
    return response.data;
}

async function getYellowApplicant(data: {params: {ApplicantUID: string}})
{
    const response = await mvteApiInstance.get<Applicant>(
            `/engauge/yellow-applicant`,
            {
                params: {
                    ApplicantUID: data.params.ApplicantUID,
                }
            },
        );

    return response.data;
}

async function getYellowTenstreetApplicant(data: {params: {StageRefID: number}})
{
    const response = await mvteApiInstance.get<YellowTenstreetApplicant>(
            `/engauge/yellow-applicant-tenstreet`,
            {
                params: {
                    StageRefID: data.params.StageRefID,
                }
            },
        );
    
    return response.data;
}

async function getYellowJobMaster(data: {params: {EmployeeUid: string}})
{
    const response = await mvteApiInstance.get<JobMaster>(
            `/engauge/yellow-jobmaster`,
            {
                params: {
                    EmployeeUid: data.params.EmployeeUid,
                }
            },
        );
    
    return response.data;
}

async function getTerminationWorkflow(data: {params: {WorkflowRequestLogID: number}})
{
    const response = await hraApiInstance.get<TerminationWorkflow[]>(
            `/hra/term-workflow`,
            {
                params: {
                    WorkflowRequestLogID: data.params.WorkflowRequestLogID,
                }
            },
        );
    
    return response.data;
}

async function getTerminationCategories(data: {params: {EmployeeType: string | undefined}})
{
    const response = await hraApiInstance.get<TerminationCategory[]>(
            `/hra/category`,
            {
                params: {
                    EmployeeType: data.params.EmployeeType,
                }
            },
        );
    
    return response.data;
}

async function getTerminationReasons()
{
    const response = await hraApiInstance.get<TerminationReason[]>(
            `/hra/reason`,
        );
    
    return response.data;
}

async function updateOrgRepresentatives(data : {
    payload: OrgPayload
})
{
    await hraApiInstance.post(
            `/hra/hr-poc`,
            {
                eorID: data.payload.eorID,
                ccID: data.payload.ccID,
                pocStaff: data.payload.pocStaff,
                pocDriver: data.payload.pocDriver,
                SourceSystem: data.payload.SourceSystem,
                ChangedBy: data.payload.ChangedBy,
                Note: data.payload.Note,
            },
        );
}


async function updateWorksiteRepresentatives(data : {
    payload: WorksitePayload
})
{
    await hraApiInstance.post(
            `/hra/hrworksite-poc`,
            {
                orgWorksiteID: data.payload.orgWorksiteID,
                pocManager: data.payload.pocManager,
                pocStaff: data.payload.pocStaff,
                pocDriver: data.payload.pocDriver,
                SourceSystem: data.payload.SourceSystem,
                ChangedBy: data.payload.ChangedBy,
                Note: data.payload.Note,
            },
        );
}

async function getDriverSupervisorMap()
{
    const response = await mvteApiInstance.get<SupervisorMap[]>(
            `/engauge/admin/driver-super-map`,
        );
    return response.data
}

async function getSupervisors()
{
    const response = await mvteApiInstance.get<Supervisor[]>(
            `/engauge/admin/supervisors`,
        );
    
        return response.data;
}

async function getWorksites()
{
    const response = await hraApiInstance.get<Worksites[]>(
            `/hra/worksites`,
        );
    
        return response.data;
}

async function getOrgWorksiteRelatinoships()
{
    const response = await hraApiInstance.get<OrgWorksiteRelationship[]>(
            `/hra/worksite-org-relationship`,
        );
    
        return response.data;
}

async function updateFleetDriverSupervisor(data : {
    payload: DriverSuperUpdatePayload
})
{
    await mvteApiInstance.put(
            `/engauge/admin/driver-supervisor`,
            {
                FleetSupervisorCode: data.payload.FleetSupervisorCode,
                NewSupervisorEmployeeUID: data.payload.NewSupervisorEmployeeUID,
                SourceSystem: data.payload.SourceSystem,
                ChangedBy: data.payload.ChangedBy,
                Note: data.payload.Note,
            },
        );
}

async function updateLastWorkDate(data: {
    payload: LastWorkDatePayload
})
{
    await hraApiInstance.post(
            `/hra/last-work-date`,
            {
                WorkflowRequestLogID: data.payload.WorkflowRequestLogID,
                NewLastWorkDate: data.payload.NewLastWorkDate,
                SourceSystem: data.payload.SourceSystem,
                ChangedByEmployeeCode: data.payload.ChangedByEmployeeCode,
                Note: data.payload.Note,
            },
    );
}

async function createFleetDriverSupervisor(data : {
    payload: DriverSuperCreatePayload
})
{
    await mvteApiInstance.post(
            `/engauge/admin/driver-fleet-supervisor`,
            {
                FleetSupervisorCode: data.payload.FleetSupervisorCode,
                SupervisorUID: data.payload.SupervisorUID,
                SourceSystem: data.payload.SourceSystem,
                ChangedBy: data.payload.ChangedBy,
                Note: data.payload.Note,
            },
        );
}

async function updateTerminationReason(data: {
    payload: TerminationReasonPayload
})
{
    await hraApiInstance.post(
            `/hra/term-reason`,
            {
                WorkflowRequestLogID: data.payload.WorkflowRequestLogID,
                TerminationReasonID: data.payload.TerminationReasonID,
                SourceSystem: data.payload.SourceSystem,
                ChangedByEmployeeCode: data.payload.ChangedByEmployeeCode,
                Note: data.payload.Note,
            },
    );
}

async function updateTerminationCategory(data: {
    payload: TerminationCategoryPayload
})
{
    await hraApiInstance.post(
            `/hra/term-category`,
            {
                WorkflowRequestLogID: data.payload.WorkflowRequestLogID,
                TerminationCategoryID: data.payload.TerminationCategoryID,
                SourceSystem: data.payload.SourceSystem,
                ChangedByEmployeeCode: data.payload.ChangedByEmployeeCode,
                Note: data.payload.Note,
            },
    );
}

async function updateTerminationNotes(data: {
    payload: TerminationNotesPayload
})
{
    await hraApiInstance.post(
            `/hra/term-notes`,
            {
                WorkflowRequestLogID: data.payload.WorkflowRequestLogID,
                TerminationNote: data.payload.TerminationNote,
                SourceSystem: data.payload.SourceSystem,
                ChangedByEmployeeCode: data.payload.ChangedByEmployeeCode,
                Note: data.payload.Note,
            },
    );
}
async function createWorksite(data: {
    payload: CreateWorksitePayload
})
{
    await hraApiInstance.post(
            `/hra/worksite`,
            {
                Code: data.payload.Code,
                State: data.payload.State,
                City: data.payload.City,
                Street: data.payload.Street,
                Location: data.payload.Location,
                AddressOne: data.payload.AddressOne,
                AddressTwo: data.payload.AddressTwo,
                ZipCode: data.payload.ZipCode,
                ITOfficeLocationName: data.payload.ITOfficeLocationName,
                isActive: data.payload.isActive,
            },
    );
}

async function createWorksiteOrganizationRelationship(data: {
    payload: WorksiteOrgRelationshipPayload
})
{
    await hraApiInstance.post(
            `/hra/worksite-relationship`,
            {
                OrganizationRelationshipID: data.payload.OrganizationRelationshipID,
                WorksiteID: data.payload.WorksiteID,
                AssignedHRManager: data.payload.AssignedHRManager,
                AssignedHRPOCStaff: data.payload.AssignedHRPOCStaff,
                AssignedHRPOCDriver: data.payload.AssignedHRPOCDriver,
            },
    );
}

const hraConfigMgmtApi =
{
    getHRAssignee,
    getOrgRepresentativeRaw,
    getWorksiteRepresentativeRaw,
    updateOrgRepresentatives,
    updateWorksiteRepresentatives,
    createFleetDriverSupervisor,
    updateFleetDriverSupervisor,
    getSupervisors,
    getDriverSupervisorMap,
    getYellow,
    getYellowApplicant,
    getYellowTenstreetApplicant,
    getYellowJobMaster,
    getTerminationWorkflow,
    updateLastWorkDate,
    getTerminationCategories,
    getTerminationReasons,
    updateTerminationReason,
    updateTerminationCategory,
    updateTerminationNotes,
    createWorksiteOrganizationRelationship,
    createWorksite,
    getOrgWorksiteRelatinoships,
    getWorksites,
}

export {hraConfigMgmtApi};